import React, {FC, Fragment, MutableRefObject, ReactNode, useEffect, useState} from "react";
import cls from "./customInnerPage.module.scss";
import {IProduct, IProductOption, ISizeChart, TranslationsType} from "../../../types/homePageTypes";
import CustomProductOptions from "./CustomProductOptions";
import ModalLayout from "../../shared/ModalLayout";
import {useDispatch, useSelector} from "react-redux";
import {IState} from "../../../types/state";
import {openModalLayoutAction} from "../../../store/general";
import SizeChartContent from "./SizeChartContent";
import shopApi from "../../../api/shop";


interface IPromotionPrice {
    applied_price_type?: string;
    currency?: string;
    discount_label?: string;
    final_price?: number | string | null;
    promotion_price?: number | string | null;
    rate?: number | string;
    regular_price?: number | string | null;
    special_price?: number | string | null;
}

interface IProps {
    product: IProduct;
    productName: string;
    productSkuOptionName: string;
    productSku: string;
    selectedCurrSymbol: string;
    children: ReactNode;
    priceHtmlAvailable: ReactNode;
    clientSidePrice: {
        price: string | number,
        specialPrice: string | number | null,
        cost: string | number
    };
    productShortDescOptionName: string;
    productShortDesc: string;
    productOptions?: Array<IProductOption>;
    selectedCurrCode: string;
    translationsData: TranslationsType;
    refsMap?: MutableRefObject<Map<number, HTMLDivElement>>;
    sizeChart: ISizeChart | null;
    dbName: string;
    domain: string
}

const CustomInfoPart: FC<IProps> = (
    {
        product,
        productName,
        productSkuOptionName,
        productSku,
        selectedCurrSymbol,
        children,
        clientSidePrice,
        priceHtmlAvailable,
        productShortDescOptionName,
        productShortDesc,
        productOptions,
        selectedCurrCode,
        translationsData,
        refsMap,
        sizeChart,
        dbName,
        domain
    }
) => {
    const openModalLayout = useSelector((state: IState) => state.general.openModalLayout);
    const customerToken = useSelector((state: IState) => state.customer.token);
    const [promotionPrice, setPromotionPrice] = useState<IPromotionPrice | null>(null);
    const dispatch = useDispatch();
    const isDonation = product?.attribute_family?.code === "Donation";

    useEffect(() => {
        let cancelled = false;

        const fetchPromotionPrice = async () => {
            if (!product?.id || !selectedCurrCode) {
                setPromotionPrice(null);
                return;
            }

            try {
                const {data} = await shopApi.getPromotionPrice(customerToken || "", `${product.id}`, selectedCurrCode) || {};

                if (!cancelled) {
                    setPromotionPrice(data && typeof data === "object" ? data : null);
                }
            } catch {
                if (!cancelled) {
                    setPromotionPrice(null);
                }
            }
        };

        fetchPromotionPrice();

        return () => {
            cancelled = true;
        };
    }, [customerToken, product?.id, selectedCurrCode]);
    const productOptionsJSX = <CustomProductOptions
        product={product}
        productOptions={productOptions}
        translationsData={translationsData}
        refsMap={refsMap}
    />
    const showProductOptionOrNot: Record<string, Record<string, ReactNode>> = {
        simple: {
            true: productOptionsJSX,
            false: <></>
        },
        virtual: {
            true: productOptionsJSX,
            false: <></>
        },
        configurable: {
            true: productOptionsJSX,
            false: <></>
        },
        downloadable: {
            true: <></>,
            false: <></>
        },
        bundle: {
            true: <></>,
            false: <></>
        },
    }
    const hasOptions = !!productOptions?.length;
    const productOptionsContent = showProductOptionOrNot[product?.type]?.[String(hasOptions)];

    const ShowPrice = () => {

        const formatPrice = (key?: string) => {
            const finalKey = key ?? "price";
            let value = (clientSidePrice as Record<string, string>)[finalKey];
            if (!value) return null
            let displayValue: string = value;

            if (finalKey === "cost") {
                if (selectedCurrCode !== "AMD") {
                    value = Number(value).toFixed(2);
                }

                displayValue = Number(value).toLocaleString();
            }

            if (selectedCurrCode === "AMD") {
                return `${displayValue} ${selectedCurrSymbol}`
            }
            return `${selectedCurrSymbol} ${displayValue}`
        }

        let className = "";
        let costTitle = null;
        let costBlock = null;

        if (clientSidePrice.cost) {
            className = cls.with_cost

            costTitle = (
                <div className={cls.price_title}>{translationsData.Cash}</div>
            );

            costBlock = (
                <div className={cls.product__price}>
                    <div className={cls.price_title}>{translationsData.NonCash}</div>
                    <div className={cls.price_value}>{formatPrice("cost")}</div>
                </div>
            );
        }

        const PriceValue = ({ whichPriceKey }: { whichPriceKey?: string }) => (
            <div className={cls.price_value}>
                {formatPrice(whichPriceKey)}
            </div>
        )

        return (
            <div className={className}>
                <div className={cls.product__price}>
                    {costTitle}
                    {clientSidePrice.specialPrice ? (
                        <>
                            <div className={cls["product-card__new-price"]}>
                                <PriceValue whichPriceKey="specialPrice" />
                            </div>
                            <div className={cls["product-card__old-price"]}>
                                <PriceValue />
                            </div>
                        </>
                    ) : (
                        <PriceValue />
                    )}
                </div>
                {costBlock}
            </div>
        )
    }

    const PromotionPriceSummary = () => {
        if (!customerToken) return null;
        if (!promotionPrice?.regular_price || !promotionPrice?.final_price) return null;
        if (promotionPrice.final_price >= promotionPrice.regular_price) return null;
        const toNumber = (value?: number | string | null) => {
            if (value === null || value === undefined || value === "") return null;
            const parsedValue = Number(String(value).replace(/,/g, ""));
            return Number.isFinite(parsedValue) ? parsedValue : null;
        };
        if (clientSidePrice?.specialPrice) {
            const promotionFinalPrice = toNumber(promotionPrice.final_price);
            const clientSpecialPrice = toNumber(clientSidePrice.specialPrice);
            if (promotionFinalPrice === null || clientSpecialPrice === null || promotionFinalPrice >= clientSpecialPrice) {
                return null;
            }
        }

        const formatPromotionPrice = (value?: number | string | null) => {
            const numericValue = toNumber(value);
            if (numericValue === null) return null;

            const formattedValue = numericValue.toLocaleString(undefined, {
                minimumFractionDigits: selectedCurrCode === "AMD" ? 0 : 2,
                maximumFractionDigits: selectedCurrCode === "AMD" ? 0 : 2,
            });

            if (selectedCurrCode === "AMD") {
                return `${formattedValue} ${selectedCurrSymbol}`;
            }

            return `${selectedCurrSymbol}${formattedValue}`;
        };

        const regularPrice = toNumber(promotionPrice.regular_price);
        const specialPrice = toNumber(promotionPrice.special_price);
        const finalPrice = toNumber(promotionPrice.final_price);
        const promotionPriceValue = toNumber(promotionPrice.promotion_price ?? promotionPrice.final_price);
        const shouldShowMyPrice = promotionPriceValue !== null
            && specialPrice ? promotionPriceValue < specialPrice : true;
        const computedSavings = shouldShowMyPrice && regularPrice
            ? `${Math.round((((finalPrice || 0) - regularPrice) / regularPrice) * 100)}%`
            : null;

        const rows: Array<{ label: string; value: string | null | undefined }> = [
            {label: translationsData.RegularPrice, value: formatPromotionPrice(promotionPrice.regular_price)},
            {label: translationsData.DiscountedPrice, value: specialPrice !== null ? formatPromotionPrice(promotionPrice.special_price) : null},
            {label: translationsData.MyPrice, value: shouldShowMyPrice ? formatPromotionPrice(promotionPrice.final_price) : null},
            {label: translationsData.MySavings, value: shouldShowMyPrice ? computedSavings : null},
        ].filter(({value}) => !!value);

        if (!rows.length) {
            return null;
        }

        return (
            <div className={cls.promotion_price_summary}>
                {rows.map(({label, value}) => (
                    <div className={cls.promotion_price_summary__row} key={label}>
                        <span>{label}</span>
                        <strong>{value}</strong>
                    </div>
                ))}
            </div>
        );
    }

    const ShowSizeChart = () => {
        if (sizeChart) {
            return <Fragment>
                <p
                    className={cls["product-inner-chart-title"]}
                    style={{ margin: "12px 0", cursor: "pointer" }}
                    onClick={() => dispatch(openModalLayoutAction(true))}
                >
                    {translationsData?.ViewSizeChart}
                </p>
                {openModalLayout
                    ? <ModalLayout close={() => dispatch(openModalLayoutAction(false))} title={sizeChart?.template_name}>
                        <SizeChartContent sizeChart={sizeChart} dbName={dbName} domain={domain}/>
                    </ModalLayout>
                    : null
                }
            </Fragment>
        }
        return null
    }

    const showShortDesc = {
        "true": <div className={cls["product-inner-description-title"]}>{productShortDescOptionName}</div>,
        "false": <></>
    }

    return (
        <Fragment>
            <h1 style={{textAlign: "left"}} className={cls.product__name}>{productName}</h1>
            <p className={cls.product__sku}>{productSkuOptionName}: {productSku}</p>
            <ShowPrice />
            <ShowSizeChart />
            <div>{priceHtmlAvailable}</div>
            {showShortDesc[`${productShortDesc?.length > 0}`]}
            <PromotionPriceSummary />
            <div dangerouslySetInnerHTML={{__html: productShortDesc}}/>
            {!isDonation && productOptionsContent}
            {children}
            {isDonation && productOptionsContent}
        </Fragment>
    )
};

export default CustomInfoPart;
